import { Tooltip, Dropdown, OverlayTrigger } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import 'font-awesome/css/font-awesome.min.css';
import {
  faEllipsis,
  faUserGraduate
} from '@fortawesome/free-solid-svg-icons';
import { faEyeSlash, faFileArrowUp, faGear, faLink, faPrint, faKey } from '@fortawesome/pro-solid-svg-icons';
import { useAssessmentContext } from '../pages/Assessments/AssessmentContext';
import AssessmentAssignModal from './assessmentFloatingMenu/AssessmentAssignModal';
import { useState } from 'react';
import CustomToast from '../../../components/Toast';
import CreateAssessmentLinkModal from './assessmentFloatingMenu/CreateAssessmentLinkModal';
import AssignSettingsPopout from './assessmentFloatingMenu/AssignSettingsPopout';
import OptionTileModal from '../pages/AssignmentsAndActivities/Components/modals/OptionTileModal';
import { useNavigate } from 'react-router-dom';
import { useExportXmlAssignmentsMutation } from '../../../features/authentication/assignApiSlice';
import { useHideAssessmentsMutation } from '../../../features/authentication/assessmentApiSlice';
import ConfirmationModal from '../../../components/ConfirmationPopUp';
import useFloatingMenuVisibility from '../../../components/floatingMenu/useFloatingMenuVisibility';

const AssessmentFloatingMenu = () => {
  const { selectedAssessments, assessmentId, setAssessmentId, assessmentName, setAssessmentName, setRefreshAssessments, setSelectedAssessments } = useAssessmentContext();
  const navigate = useNavigate();
  const [exportAssessments] = useExportXmlAssignmentsMutation();
  const [hideAssessments] = useHideAssessmentsMutation();
  const floatingMenu = [
    {
      id: 1,
      name: 'Assign',
      tooltip: 'Assign one or more Assessments to your students',
      icon: <FontAwesomeIcon icon={faUserGraduate} />,
      isMultiSelect: true,
      modal: () => handleAssignToStudents()
    },
    {
      id: 2,
      name: 'Setttings',
      tooltip: 'Assessment Settings',
      icon: <FontAwesomeIcon icon={faGear} />,
      isMultiSelect: true,
      modal: () => handleAssignSettings()
    },
    {
      id: 3,
      name: 'Create Link',
      tooltip: 'Create Assessment Link',
      icon: <FontAwesomeIcon icon={faLink} />,
      isMultiSelect: true,
      modal: () => handleCreateAssessmentLink()
    },
    {
      id: 4,
      name: 'Print',
      tooltip: 'Print Assessment',
      icon: <FontAwesomeIcon icon={faPrint} />,
      isMultiSelect: false,
      modal: () => handlePrintAssessment()
    },
    {
      id: 5,
      name: 'Export',
      tooltip: 'Export Assessment',
      icon: <FontAwesomeIcon icon={faFileArrowUp} />,
      isMultiSelect: true,
      modal: () => handleExportAssessments()
    },
    {
      id: 6,
      name: 'Hide',
      tooltip: 'Hide Assessment',
      icon: <FontAwesomeIcon icon={faEyeSlash} />,
      isMultiSelect: true,
      modal: () => handleHideAssessments()
    }
  ];
  const printingOptions = [
    {
      id: 'standardPrinting',
      icon: <FontAwesomeIcon icon={faPrint} className="printing-icon" />,
      label: 'Standard Assessment Printing',
      isActive: true
    },
    {
      id: 'answerKey',
      icon: <FontAwesomeIcon icon={faKey} className="printing-icon" />,
      label: 'Answer Key'
    }
  ];
  const [isOpenAssessmentAssignModal, setIsOpenAssessmentAssignModal] = useState(false);
  const [isOpenCreateAssessmentLinkModal, setIsOpenCreateAssessmentLinkModal] = useState(false);
  const [isOpenAssignmentSettings, setIsOpenAssignmentSettings] = useState(false);
  const [isOpenPrintingOptionsModal, setIsOpenPrintingOptionsModal] = useState(false);
  const [isOpenHideConfirmationModal, setIsOpenHideConfirmationModal] = useState(false);
  const [printOptions, setPrintOptions] = useState(printingOptions);
  const [warningText, setWarningText] = useState('');
  const [navigation, setNavigation] = useState('');
  const { visibleButtons, visibleCountOnScreen, showMoreMenu } = useFloatingMenuVisibility(floatingMenu);

  const floatingMenuHandler = (value) => {
    if (!value.isMultiSelect && selectedAssessments.length > 1)
      return CustomToast(value.tooltip + ' does not support Multi Selection.', 'info');

    if (selectedAssessments?.length > 0) {
      value.modal();
    } else {
      CustomToast('You have not selected any assessments.', 'info');
    }
  };

  const handleAssignToStudents = () => {
    setIsOpenAssessmentAssignModal(true);
  };

  const handleCreateAssessmentLink = () => {
    setIsOpenCreateAssessmentLinkModal(true);
  };

  const handleAssignSettings = () => {
    setIsOpenAssignmentSettings(!isOpenAssignmentSettings);
  };

  const handleAssignModalOnClose = () => {
    setIsOpenAssessmentAssignModal(false);
  };

  const handleCreateLinkModalOnClose = () => {
    setIsOpenCreateAssessmentLinkModal(false);
  };

  const handleAssignSettingsPopoutOnClose = () => {
    setIsOpenAssignmentSettings(false);
  };

  const handlePrintAssessment = () => {
    setAssessmentId(selectedAssessments[0]?.assignmentId);
    setAssessmentName(selectedAssessments[0]?.assignmentName);
    setIsOpenPrintingOptionsModal(!isOpenPrintingOptionsModal);
    setNavigation(`${selectedAssessments[0]?.assignmentId}/standardPrinting/true`);
    setPrintOptions(printingOptions);
  };

  const handlePrintingOptions = (e) => {
    const id = e.currentTarget.id;
    const option = printOptions.map((item) =>
      item.id === id ? { ...item, isActive: true } : { ...item, isActive: false }
    );

    if (id === 'standardPrinting') {
      const url = `${assessmentId}/standardPrinting/true`;
      setNavigation(url);
    } else {
      const url = `${assessmentId}/answerKey/true`;
      setNavigation(url);
    }
    setPrintOptions(option);
  };

  const handlePrintOptionNavigation = () => {
    navigate(navigation, { state: { assignmentId: assessmentId, assignmentName: assessmentName } });
  };

  const handleExportAssessments = async () => {
    try {
      const option = selectedAssessments?.map((assessment) => {
        return assessment.assignmentId;
      });
      await exportAssessments(option).unwrap();
    } catch (error) {
      if (error.data) {
        const XMLData = error.data;
        const blob = new Blob([XMLData], { type: 'text/xml' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const fileName = 'CastleXMLAssessments.xml';
        link.download = fileName;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
      }
    }
  };

  const handleHideAssessments = async () => {
    setIsOpenHideConfirmationModal(true);
    let assessmentString = '';
    if (selectedAssessments.length === 1) {
      assessmentString = selectedAssessments[0]?.assignmentName;
    } else if (selectedAssessments.length <= 3) {
      assessmentString = selectedAssessments.map(a => a.assignmentName).join(', ');
    }
    setWarningText(`<strong>Are you sure you want to Hide selected Assessment${selectedAssessments.length > 1 ? 's' : ''}?</strong></br>${assessmentString}`);
  };

  const hideSelectedAssessments = async () => {
    try {
      const options = {
        assessmentIds: selectedAssessments.map(a => a.assignmentId)
      };

      var response = await hideAssessments(options);
      if (response.error) {
        if (response.error.data.errors)
          CustomToast(response.error.data.errors.AssessmentIds, 'error');
        else
          CustomToast(response.error.data.error, 'error');
      } else {
        setSelectedAssessments([]);
        setRefreshAssessments(new Date().getSeconds());
        CustomToast('Assessment Hidden Successfully!', 'success');
      }
      setIsOpenHideConfirmationModal(false);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <>
      <div className="floating-menu-in">
        {visibleButtons.map((value, index) => (
          <OverlayTrigger
            key={`float-top-${value.id}`}
            placement="top"
            overlay={
              <Tooltip id={`tooltip-top-${value.id}`} className="custom-tooltip">
                {value.tooltip}
              </Tooltip>
            }>
            <button
              className={!showMoreMenu && index === visibleCountOnScreen - 1 ? 'floating-menu-last-visible' : ''}
              id={`floating-menu-${value.id}`}
              onClick={() => floatingMenuHandler(value)}
            >
              {value.icon}
              {value.name}
            </button>
          </OverlayTrigger>
        ))}

        {showMoreMenu && <Dropdown>
          <Dropdown.Toggle className='floating-menu-last-visible'>
            <FontAwesomeIcon icon={faEllipsis} />
            More
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {visibleButtons.map((value, index) => (
              <Dropdown.Item
                key={`float-menu-${index}`}
                onClick={() => floatingMenuHandler(value)}>
                {value.icon} {value.name}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>}
      </div>

      {isOpenAssessmentAssignModal &&
        <AssessmentAssignModal
          isOpen={isOpenAssessmentAssignModal}
          onClose={handleAssignModalOnClose}
          selectedAssessments={selectedAssessments}
        />
      }

      {isOpenCreateAssessmentLinkModal &&
        <CreateAssessmentLinkModal
          isOpen={isOpenCreateAssessmentLinkModal}
          onClose={handleCreateLinkModalOnClose}
          selectedAssessments={selectedAssessments}
        />
      }

      {isOpenAssignmentSettings &&
        <AssignSettingsPopout
          isOpen={isOpenAssignmentSettings}
          onClose={handleAssignSettingsPopoutOnClose}
          selectedAssessments={selectedAssessments}
        />
      }

      {isOpenPrintingOptionsModal &&
        <OptionTileModal
          isOpen={isOpenPrintingOptionsModal}
          onClose={() => setIsOpenPrintingOptionsModal(false)}
          title={'Select Print Option'}
          options={printOptions}
          handleOptionClick={handlePrintingOptions}
          saveLabel={'Continue'}
          handleSaveClick={handlePrintOptionNavigation}
        />
      }

      {isOpenHideConfirmationModal &&
        <ConfirmationModal
          isOpen={isOpenHideConfirmationModal}
          onClose={() => setIsOpenHideConfirmationModal(false)}
          titleName={`Hide Assessment${selectedAssessments.length > 1 ? 's' : ''}`}
          onSave={() => hideSelectedAssessments()}
          cancelButton='No'
          confirmButton='Yes'
          contentWidth='30%'
          buttonPosition='center'
          content={warningText}
        />}
    </>
  );
};

export default AssessmentFloatingMenu;
